import { useMemo, useState } from "react";
import { Search, Trash2, X } from "lucide-react";

/* ---------------- Hook ----------------
 * Wraps a list of items with:
 *   - live search over user-picked fields
 *   - multi-select state (toggle, toggleAll, clear)
 *
 * Usage:
 *   const t = useTableTools(items, ["name","email","title"]);
 *   t.filtered            // filtered items
 *   t.selectedIds         // Set<string>
 *   t.toggle(id)          // toggle one
 *   t.toggleAll(list)     // if all in `list` are selected -> clear else select all
 *   t.clear()             // clear selection
 *   t.search / t.setSearch
 */
export function useTableTools(items, searchKeys = []) {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items || [];
    return (items || []).filter((it) =>
      searchKeys.some((k) => {
        const v = it?.[k];
        if (v == null) return false;
        return String(v).toLowerCase().includes(q);
      })
    );
  }, [items, search, searchKeys]);

  const toggle = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = (list) => {
    const ids = (list || []).map((x) => x.id).filter(Boolean);
    setSelectedIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const clear = () => setSelectedIds(new Set());

  return { filtered, search, setSearch, selectedIds, toggle, toggleAll, clear };
}

/* ---------------- Toolbar ---------------- */
export function TableToolbar({
  search,
  onSearchChange,
  placeholder = "Search…",
  selectedCount = 0,
  onBulkDelete,
  onClearSelection,
  testidPrefix = "table",
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[220px] max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={placeholder}
          data-testid={`${testidPrefix}-search`}
          className="w-full bg-background border border-border rounded-full pl-9 pr-3 py-2 text-sm outline-none focus:border-primary/60"
        />
      </div>
      {selectedCount > 0 && (
        <>
          <button
            onClick={onBulkDelete}
            data-testid={`${testidPrefix}-bulk-delete`}
            className="rounded-full px-4 py-2 bg-destructive text-destructive-foreground text-xs font-medium inline-flex items-center gap-2 hover:opacity-90"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete selected ({selectedCount})
          </button>
          <button
            onClick={onClearSelection}
            data-testid={`${testidPrefix}-clear-selection`}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        </>
      )}
    </div>
  );
}

/* ---------------- Row / header select boxes ---------------- */
export function SelectAllCell({ items, selectedIds, onToggleAll, testid = "select-all" }) {
  const ids = items.map((x) => x.id).filter(Boolean);
  const checked = ids.length > 0 && ids.every((id) => selectedIds.has(id));
  const indeterminate = !checked && ids.some((id) => selectedIds.has(id));
  return (
    <th className="p-4 w-10">
      <input
        type="checkbox"
        checked={checked}
        ref={(el) => { if (el) el.indeterminate = indeterminate; }}
        onChange={() => onToggleAll(items)}
        data-testid={testid}
        className="rounded border-border cursor-pointer"
      />
    </th>
  );
}

export function SelectRowCell({ id, selectedIds, onToggle, testid }) {
  return (
    <td className="p-4 w-10">
      <input
        type="checkbox"
        checked={selectedIds.has(id)}
        onChange={() => onToggle(id)}
        data-testid={testid}
        onClick={(e) => e.stopPropagation()}
        className="rounded border-border cursor-pointer"
      />
    </td>
  );
}

/* ---------------- Bulk deleter ----------------
 * Given a list of ids and a deleteOne(id) function, deletes them in parallel
 * and calls onDone(count) when finished. Shows toast confirmation via caller. */
export async function bulkDeleteIds(ids, deleteOne) {
  const arr = Array.from(ids);
  if (!arr.length) return 0;
  const results = await Promise.allSettled(arr.map((id) => deleteOne(id)));
  return results.filter((r) => r.status === "fulfilled").length;
}
